import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './planner.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const EVENT_TYPES = [
  ['LARGE_PUJA', 'Large puja'],
  ['HOME_PUJA', 'Home puja'],
  ['BIRTHDAY', 'Birthday'],
  ['ANNIVERSARY', 'Anniversary'],
  ['FUN_PARTY', 'Fun party'],
  ['CULTURAL', 'Cultural event'],
  ['COMMUNITY', 'Community event'],
  ['OTHER', 'Other'],
];

const TIMEZONES = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Toronto', 'Europe/London', 'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore'];
const emptyEventForm = { title: '', eventType: 'COMMUNITY', eventDate: '', startTime: '', endTime: '', timezone: 'America/New_York', locationType: 'IN_PERSON', venue: '', onlineEventUrl: '', guestCapacity: '100', isCharitable: false, description: '', publicationStatus: 'DRAFT' };
const bannerSrc = (value) => value ? `${API_BASE}${value}` : '';

const request = async (path, options = {}) => {
  const response = await fetch(`${API_BASE}/api/planner${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || 'Something went wrong.');
  return data;
};

const formatDate = (value) => {
  if (!value) return 'Date to be decided';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${value}T00:00:00Z`));
};

const statusLabel = (value) => String(value || '').replaceAll('_', ' ').toLowerCase().replace(/^./, (letter) => letter.toUpperCase());

const EmptyState = ({ onCreate }) => (
  <section className="planner-empty">
    <span className="planner-empty__mark" aria-hidden="true">✦</span>
    <p className="planner-eyebrow">Your first event</p>
    <h2>Turn the next gathering into a clear plan.</h2>
    <p>Create an event, add the first tasks, and watch the plan move forward.</p>
    <button className="planner-button planner-button--primary" onClick={onCreate}>Create an event</button>
  </section>
);

const SubscriptionChoice = ({ plans, busy, error, onChoose }) => (
  <main className="planner-onboarding">
    <section className="planner-onboarding__intro">
      <span className="planner-brand-mark" aria-hidden="true">UC</span>
      <p className="planner-eyebrow">A member benefit from Udyog Connect</p>
      <h1>Plan meaningful events with less guesswork.</h1>
      <p>Bring the schedule, responsibilities, registrations, and vendors into one calm workspace. Start with the essentials today.</p>
    </section>
    {error && <div className="planner-alert" role="alert">{error}</div>}
    <section className="planner-plan-grid" aria-label="Planner subscription options">
      {plans.map((plan) => (
        <article className={`planner-plan-card ${plan.code === 'PAID' ? 'is-featured' : ''}`} key={plan.code}>
          {plan.code === 'PAID' && <span className="planner-plan-card__flag">Coming next</span>}
          <p className="planner-eyebrow">{plan.code === 'FREE' ? 'Start planning' : 'For active organizers'}</p>
          <h2>{plan.name}</h2>
          <div className="planner-plan-price">
            {plan.priceMonthly === 0 ? <strong>$0</strong> : <strong>Paid</strong>}
            <span>{plan.priceMonthly === 0 ? 'forever' : 'pricing to be announced'}</span>
          </div>
          <ul>{plan.features.map((feature) => <li key={feature}>✓ {feature}</li>)}</ul>
          <button
            className={`planner-button ${plan.code === 'FREE' ? 'planner-button--primary' : 'planner-button--quiet'}`}
            disabled={busy || plan.code === 'PAID'}
            onClick={() => onChoose(plan.code)}
          >
            {plan.code === 'FREE' ? (busy ? 'Activating…' : 'Activate Free') : 'Join waitlist soon'}
          </button>
        </article>
      ))}
    </section>
  </main>
);

const EventForm = ({ open, busy, initialValues, onClose, onSave, onRemoveBanner }) => {
  const [form, setForm] = useState(emptyEventForm);
  const [bannerFile, setBannerFile] = useState(null);
  useEffect(() => {
    if (!open) return;
    setForm(initialValues ? {
      title: initialValues.title || '', eventType: initialValues.event_type || 'COMMUNITY', eventDate: initialValues.event_date || '',
      startTime: initialValues.start_time || '', endTime: initialValues.end_time || '', timezone: initialValues.timezone || 'America/New_York',
      locationType: initialValues.location_type || 'IN_PERSON', venue: initialValues.venue || '', onlineEventUrl: initialValues.online_event_url || '',
      guestCapacity: String(initialValues.guest_capacity || 100), isCharitable: Boolean(initialValues.is_charitable), description: initialValues.description || '',
      publicationStatus: initialValues.publication_status || 'DRAFT',
    } : emptyEventForm);
    setBannerFile(null);
  }, [open, initialValues]);
  const update = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  const submit = async (event) => {
    event.preventDefault();
    const saved = await onSave(form, bannerFile);
    if (saved) {
      setForm(emptyEventForm);
      onClose();
    }
  };
  if (!open) return null;
  return (
    <div className="planner-modal-backdrop" role="presentation">
      <section className="planner-modal" role="dialog" aria-modal="true" aria-labelledby="planner-event-form-title">
        <header><div><p className="planner-eyebrow">{initialValues ? 'Event settings' : 'New plan'}</p><h2 id="planner-event-form-title">{initialValues ? 'Edit event' : 'Create an event'}</h2></div><button className="planner-icon-button" onClick={onClose} aria-label="Close">×</button></header>
        <form onSubmit={submit}>
          <label className="planner-field planner-field--wide"><span>Event name *</span><input name="title" value={form.title} onChange={update} maxLength="180" placeholder="e.g. Diwali Community Celebration" required autoFocus /></label>
          <label className="planner-field"><span>Event type *</span><select name="eventType" value={form.eventType} onChange={update}>{EVENT_TYPES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
          <label className="planner-field"><span>Event date</span><input type="date" name="eventDate" value={form.eventDate} onChange={update} /></label>
          <label className="planner-field"><span>Starts</span><input type="time" name="startTime" value={form.startTime} onChange={update} /></label>
          <label className="planner-field"><span>Ends</span><input type="time" name="endTime" value={form.endTime} onChange={update} /></label>
          <label className="planner-field"><span>Timezone</span><select name="timezone" value={form.timezone} onChange={update}>{TIMEZONES.map((zone) => <option value={zone} key={zone}>{zone.replaceAll('_', ' ')}</option>)}</select></label>
          <label className="planner-field"><span>Location type</span><select name="locationType" value={form.locationType} onChange={update}><option value="IN_PERSON">In person</option><option value="ONLINE">Online</option><option value="HYBRID">Hybrid</option></select></label>
          {form.locationType !== 'ONLINE' && <label className="planner-field"><span>Venue</span><input name="venue" value={form.venue} onChange={update} maxLength="220" placeholder="Venue or home" /></label>}
          {form.locationType !== 'IN_PERSON' && <label className="planner-field"><span>Online-event link</span><input type="url" name="onlineEventUrl" value={form.onlineEventUrl} onChange={update} maxLength="1000" placeholder="https://…" /></label>}
          <label className="planner-field"><span>Registration capacity</span><input type="number" min="1" max="100" name="guestCapacity" value={form.guestCapacity} onChange={update} placeholder="100" /></label>
          <label className="planner-field"><span>Visibility</span><select name="publicationStatus" value={form.publicationStatus} onChange={update}><option value="DRAFT">Draft — private</option><option value="PUBLISHED">Published — public</option></select></label>
          <label className="planner-charity-toggle planner-field--wide"><input type="checkbox" name="isCharitable" checked={form.isCharitable} onChange={(event) => setForm((current) => ({ ...current, isCharitable: event.target.checked }))} /><span><strong>This is a charitable event</strong><small>Add volunteer interest and availability to the registration form.</small></span></label>
          <label className="planner-field planner-field--wide"><span>What are you planning?</span><textarea name="description" value={form.description} onChange={update} maxLength="4000" rows="3" placeholder="A short description of the event and its goals." /></label>
          <label className="planner-field planner-field--wide"><span>Event banner</span><input type="file" accept="image/jpeg,image/png,image/webp" onChange={(event) => setBannerFile(event.target.files?.[0] || null)} /><small>JPG, PNG, or WebP · up to 6 MB</small></label>
          {initialValues?.banner_url && <div className="planner-banner-editor planner-field--wide"><img src={bannerSrc(initialValues.banner_url)} alt="Current event banner" /><button type="button" className="planner-button planner-button--danger" onClick={onRemoveBanner}>Remove banner</button></div>}
          <div className="planner-modal__actions"><button type="button" className="planner-button planner-button--quiet" onClick={onClose}>Cancel</button><button className="planner-button planner-button--primary" disabled={busy}>{busy ? 'Saving…' : initialValues ? 'Save event' : 'Create event'}</button></div>
        </form>
      </section>
    </div>
  );
};

const PlannerApp = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [plan, setPlan] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [activeTab, setActiveTab] = useState('tasks');
  const [copied, setCopied] = useState(false);
  const [showEventForm, setShowEventForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', ownerName: '', dueDate: '' });
  const [editingTaskId, setEditingTaskId] = useState(null);

  const selectedEvent = useMemo(() => events.find((event) => Number(event.id) === Number(selectedId)) || null, [events, selectedId]);

  const loadEvents = useCallback(async () => {
    const data = await request('/events');
    setEvents(data.events || []);
    setPlan(data.plan || null);
    setSelectedId((current) => current || data.events?.[0]?.id || null);
  }, []);

  const loadTasks = useCallback(async (eventId) => {
    if (!eventId) { setTasks([]); return; }
    const data = await request(`/events/${eventId}/tasks`);
    setTasks(data.tasks || []);
  }, []);

  const loadRegistrations = useCallback(async (eventId) => {
    if (!eventId) { setRegistrations([]); return; }
    const data = await request(`/events/${eventId}/registrations`);
    setRegistrations(data.registrations || []);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [plansData, subscriptionData] = await Promise.all([request('/plans'), request('/subscription')]);
        setPlans(plansData.plans || []);
        setSubscription(subscriptionData.subscription);
        setPlan(subscriptionData.plan);
        if (subscriptionData.subscription?.subscription_status === 'ACTIVE') await loadEvents();
      } catch (loadError) {
        setError(loadError.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadEvents]);

  useEffect(() => {
    if (!selectedId || !subscription) return;
    Promise.all([loadTasks(selectedId), loadRegistrations(selectedId)]).catch((loadError) => setError(loadError.message));
  }, [selectedId, subscription, loadTasks, loadRegistrations]);

  const activatePlan = async (planCode) => {
    setBusy(true); setError('');
    try {
      const data = await request('/subscription', { method: 'POST', body: JSON.stringify({ planCode }) });
      setSubscription(data.subscription); setPlan(data.plan); await loadEvents();
    } catch (activationError) { setError(activationError.message); }
    finally { setBusy(false); }
  };

  const saveEvent = async (values, bannerFile) => {
    setBusy(true); setError('');
    try {
      const data = editingEvent
        ? await request(`/events/${editingEvent.id}/details`, { method: 'PUT', body: JSON.stringify(values) })
        : await request('/events', { method: 'POST', body: JSON.stringify(values) });
      const eventId = editingEvent?.id || data.id;
      if (bannerFile) {
        const formData = new FormData(); formData.append('banner', bannerFile);
        const response = await fetch(`${API_BASE}/api/planner/events/${eventId}/banner`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, body: formData });
        const uploadData = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(uploadData.message || 'The event saved, but the banner upload failed.');
      }
      await loadEvents(); setSelectedId(eventId); setEditingEvent(null); return true;
    } catch (createError) { setError(createError.message); return false; }
    finally { setBusy(false); }
  };

  const removeEventBanner = async () => {
    if (!editingEvent?.id || !window.confirm('Remove this event banner?')) return;
    setBusy(true); setError('');
    try {
      await request(`/events/${editingEvent.id}/banner`, { method: 'DELETE' });
      await loadEvents(); setEditingEvent((current) => ({ ...current, banner_url: null }));
    } catch (bannerError) { setError(bannerError.message); }
    finally { setBusy(false); }
  };

  const addTask = async (event) => {
    event.preventDefault();
    if (!taskForm.title.trim()) return;
    setBusy(true); setError('');
    try {
      if (editingTaskId) await request(`/tasks/${editingTaskId}`, { method: 'PUT', body: JSON.stringify(taskForm) });
      else await request(`/events/${selectedId}/tasks`, { method: 'POST', body: JSON.stringify(taskForm) });
      setTaskForm({ title: '', ownerName: '', dueDate: '' });
      setEditingTaskId(null);
      await Promise.all([loadTasks(selectedId), loadEvents()]);
    } catch (taskError) { setError(taskError.message); }
    finally { setBusy(false); }
  };

  const editTask = (task) => {
    setEditingTaskId(task.id);
    setTaskForm({ title: task.title, ownerName: task.owner_name || '', dueDate: task.due_date || '' });
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete “${task.title}”? This cannot be undone.`)) return;
    setError('');
    try {
      await request(`/tasks/${task.id}`, { method: 'DELETE' });
      if (Number(editingTaskId) === Number(task.id)) { setEditingTaskId(null); setTaskForm({ title: '', ownerName: '', dueDate: '' }); }
      await Promise.all([loadTasks(selectedId), loadEvents()]);
    } catch (taskError) { setError(taskError.message); }
  };

  const updateTask = async (taskId, taskStatus) => {
    setError('');
    try {
      await request(`/tasks/${taskId}`, { method: 'PUT', body: JSON.stringify({ taskStatus }) });
      await Promise.all([loadTasks(selectedId), loadEvents()]);
    } catch (taskError) { setError(taskError.message); }
  };

  const updateEventStatus = async (eventStatus) => {
    if (eventStatus === 'CANCELLED' && !window.confirm('Cancel this event? Registration will close and its public page will be removed.')) return;
    if (eventStatus === 'ARCHIVED' && !window.confirm('Archive this event? It will no longer count toward your active event limit.')) return;
    setError('');
    try {
      await request(`/events/${selectedId}`, { method: 'PUT', body: JSON.stringify({ eventStatus }) });
      await loadEvents();
    } catch (statusError) { setError(statusError.message); }
  };

  const updateRegistrationAvailability = async (registrationStatus) => {
    setError('');
    try {
      await request(`/events/${selectedId}/registration-status`, { method: 'PUT', body: JSON.stringify({ registrationStatus }) });
      await loadEvents();
    } catch (statusError) { setError(statusError.message); }
  };

  const updateRegistrationStatus = async (registrationId, registrationStatus) => {
    setError('');
    try {
      await request(`/registrations/${registrationId}/status`, { method: 'PUT', body: JSON.stringify({ registrationStatus }) });
      await Promise.all([loadRegistrations(selectedId), loadEvents()]);
    } catch (statusError) { setError(statusError.message); }
  };

  const deleteRegistrationData = async (registration) => {
    if (!window.confirm(`Delete personal information for ${registration.full_name}? The name, contact details, notes, and volunteer details will be permanently anonymized.`)) return;
    setError('');
    try {
      await request(`/registrations/${registration.id}/personal-data`, { method: 'DELETE' });
      await Promise.all([loadRegistrations(selectedId), loadEvents()]);
    } catch (privacyError) { setError(privacyError.message); }
  };

  const exportRegistrations = async () => {
    setError('');
    try {
      const response = await fetch(`${API_BASE}/api/planner/events/${selectedId}/registrations.csv`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (!response.ok) { const data = await response.json().catch(() => ({})); throw new Error(data.message || 'Export failed.'); }
      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filename = disposition.match(/filename="([^"]+)"/)?.[1] || 'planner-registrations.csv';
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = filename; document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch (exportError) { setError(exportError.message); }
  };

  const registrationUrl = selectedEvent?.public_key ? `${window.location.origin}/2.0/planner/event/${selectedEvent.public_key}` : '';
  const copyRegistrationLink = async () => {
    try { await navigator.clipboard.writeText(registrationUrl); setCopied(true); window.setTimeout(() => setCopied(false), 1800); }
    catch (copyError) { setError('Copy was blocked. Select the registration link and copy it manually.'); }
  };

  if (loading) return <div className="planner-loading"><span></span><p>Opening your planner…</p></div>;
  if (!subscription) return <SubscriptionChoice plans={plans} busy={busy} error={error} onChoose={activatePlan} />;

  return (
    <main className="planner-app">
      <header className="planner-hero">
        <div><p className="planner-eyebrow">UC Event Planner</p><h1>Plan together. Celebrate fully.</h1><p>Your member workspace for gatherings, ceremonies, and community events.</p></div>
        <div className="planner-hero__actions"><span className="planner-plan-badge">{plan?.name || 'Free'} plan</span><button className="planner-button planner-button--primary" onClick={() => { setEditingEvent(null); setShowEventForm(true); }}>＋ New event</button></div>
      </header>

      {error && <div className="planner-alert" role="alert"><span>{error}</span><button onClick={() => setError('')} aria-label="Dismiss">×</button></div>}

      {!events.length ? <EmptyState onCreate={() => setShowEventForm(true)} /> : (
        <div className="planner-workspace">
          <aside className="planner-events-panel">
            <div className="planner-section-heading"><div><p className="planner-eyebrow">My events</p><h2>{events.length} of {plan?.eventLimit ?? '∞'} used</h2></div></div>
            <div className="planner-event-list">
              {events.map((item) => (
                <button key={item.id} className={`planner-event-card ${Number(item.id) === Number(selectedId) ? 'is-active' : ''}`} onClick={() => setSelectedId(item.id)}>
                  <span className={`planner-status-dot is-${item.event_status.toLowerCase()}`}></span>
                  <span className="planner-event-card__content"><strong>{item.title}</strong><small>{statusLabel(item.publication_status)} · {formatDate(item.event_date)}{item.venue ? ` · ${item.venue}` : ''}</small><span><i style={{ width: `${item.progress}%` }}></i></span></span>
                  <em>{item.progress}%</em>
                </button>
              ))}
            </div>
            <section className="planner-upgrade-card"><span aria-hidden="true">◇</span><div><strong>Need more room?</strong><p>Planner Plus will add advanced forms, stakeholders, vendors, and unlimited events.</p><button disabled>Upgrade coming soon</button></div></section>
          </aside>

          {selectedEvent && <section className="planner-detail">
            <header className="planner-detail__header">
              <div>{selectedEvent.banner_url && <img className="planner-detail-banner" src={bannerSrc(selectedEvent.banner_url)} alt="" />}<span className="planner-event-type">{statusLabel(selectedEvent.event_type)} · {statusLabel(selectedEvent.publication_status)}</span><h2>{selectedEvent.title}</h2><p>{formatDate(selectedEvent.event_date)}{selectedEvent.start_time ? ` · ${selectedEvent.start_time}${selectedEvent.end_time ? `–${selectedEvent.end_time}` : ''} ${selectedEvent.timezone || ''}` : ''}{selectedEvent.venue ? ` · ${selectedEvent.venue}` : ''}{selectedEvent.location_type === 'ONLINE' ? ' · Online' : ''}{selectedEvent.guest_capacity ? ` · ${selectedEvent.guest_capacity} guests` : ''}</p><button className="planner-text-button" onClick={() => { setEditingEvent(selectedEvent); setShowEventForm(true); }}>Edit event details</button></div>
              <label className="planner-status-select"><span>Lifecycle</span><select value={selectedEvent.event_status} onChange={(event) => updateEventStatus(event.target.value)}><option value="PLANNING">Planning</option><option value="ACTIVE">Active</option><option value="COMPLETED">Completed</option><option value="CANCELLED">Cancelled</option><option value="ARCHIVED">Archived</option></select></label>
            </header>
            <section className="planner-registration-bar">
              <div><strong>{selectedEvent.registered_seats} / {selectedEvent.guest_capacity || plan?.attendeeLimit || 100}</strong><span>participants registered</span>{Boolean(selectedEvent.is_charitable) && <em>Volunteer signup enabled</em>}</div>
              <div><label className="planner-registration-toggle"><span>Registration</span><select value={selectedEvent.registration_status} onChange={(event) => updateRegistrationAvailability(event.target.value)} disabled={['CANCELLED', 'ARCHIVED'].includes(selectedEvent.event_status)}><option value="OPEN">Open</option><option value="CLOSED">Closed</option></select></label><input aria-label="Public registration link" value={selectedEvent.publication_status === 'PUBLISHED' ? registrationUrl : 'Publish this event to create a public page'} readOnly /><button className="planner-button planner-button--quiet" onClick={copyRegistrationLink} disabled={selectedEvent.publication_status !== 'PUBLISHED'}>{copied ? 'Copied!' : 'Copy link'}</button>{selectedEvent.publication_status === 'PUBLISHED' ? <a className="planner-button planner-button--primary" href={registrationUrl} target="_blank" rel="noreferrer">View page</a> : <button className="planner-button planner-button--primary" onClick={() => { setEditingEvent(selectedEvent); setShowEventForm(true); }}>Publish</button>}</div>
            </section>
            <div className="planner-progress-card"><div><strong>{selectedEvent.progress}%</strong><span>complete</span></div><div><p>{selectedEvent.completed_task_count} of {selectedEvent.task_count} tasks finished</p><span><i style={{ width: `${selectedEvent.progress}%` }}></i></span></div></div>
            <nav className="planner-detail-tabs" aria-label="Event workspace"><button className={activeTab === 'tasks' ? 'is-active' : ''} onClick={() => setActiveTab('tasks')}>Tasks</button><button className={activeTab === 'registrations' ? 'is-active' : ''} onClick={() => setActiveTab('registrations')}>Registrations <span>{registrations.length}</span></button></nav>
            {activeTab === 'tasks' ? <section className="planner-task-section">
              <div className="planner-section-heading"><div><p className="planner-eyebrow">Action plan</p><h3>Tasks</h3></div><span>{tasks.length} of {plan?.tasksPerEventLimit ?? '∞'}</span></div>
              <form className="planner-task-form" onSubmit={addTask}>
                <input aria-label="Task title" placeholder="Add the next task…" value={taskForm.title} onChange={(event) => setTaskForm({ ...taskForm, title: event.target.value })} maxLength="180" required />
                <input aria-label="Task owner" placeholder="Owner" value={taskForm.ownerName} onChange={(event) => setTaskForm({ ...taskForm, ownerName: event.target.value })} maxLength="140" />
                <input aria-label="Task due date" type="date" value={taskForm.dueDate} onChange={(event) => setTaskForm({ ...taskForm, dueDate: event.target.value })} />
                <button className="planner-button planner-button--primary" disabled={busy}>{editingTaskId ? 'Save task' : 'Add task'}</button>
                {editingTaskId && <button type="button" className="planner-button planner-button--quiet" onClick={() => { setEditingTaskId(null); setTaskForm({ title: '', ownerName: '', dueDate: '' }); }}>Cancel edit</button>}
              </form>
              <div className="planner-task-list">
                {!tasks.length && <p className="planner-task-empty">No tasks yet. Add the first step above.</p>}
                {tasks.map((task) => (
                  <article className={`planner-task is-${task.task_status.toLowerCase()}`} key={task.id}>
                    <button className="planner-task__check" onClick={() => updateTask(task.id, task.task_status === 'DONE' ? 'TODO' : 'DONE')} aria-label={task.task_status === 'DONE' ? `Reopen ${task.title}` : `Complete ${task.title}`}>{task.task_status === 'DONE' ? '✓' : ''}</button>
                    <div><strong>{task.title}</strong><span>{task.owner_name || 'Unassigned'}{task.due_date ? ` · Due ${formatDate(task.due_date)}` : ''}</span></div>
                    <select aria-label={`Status for ${task.title}`} value={task.task_status} onChange={(event) => updateTask(task.id, event.target.value)}><option value="TODO">To do</option><option value="IN_PROGRESS">In progress</option><option value="DONE">Done</option></select><div className="planner-task__actions"><button onClick={() => editTask(task)}>Edit</button><button className="is-danger" onClick={() => deleteTask(task)}>Delete</button></div>
                  </article>
                ))}
              </div>
            </section> : <section className="planner-registration-section">
              <div className="planner-section-heading"><div><p className="planner-eyebrow">Guest management</p><h3>Registrations</h3></div><div className="planner-registration-actions"><span>{selectedEvent.registered_seats} of {selectedEvent.guest_capacity || 100} seats</span><button className="planner-button planner-button--quiet" onClick={exportRegistrations} disabled={!registrations.length}>Export CSV</button></div></div>
              {!registrations.length ? <p className="planner-task-empty">No registrations yet. Share the public link to invite participants.</p> : <div className="planner-registration-list">{registrations.map((registration) => <article className={registration.registration_status === 'CANCELLED' ? 'is-cancelled' : ''} key={registration.id}><span className="planner-registration-avatar">{registration.full_name.slice(0, 1).toUpperCase()}</span><div><strong>{registration.full_name}</strong><small>{registration.email}{registration.phone ? ` · ${registration.phone}` : ''}{registration.retention_expires_at ? ` · Retain until ${registration.retention_expires_at}` : ''}</small></div><span>{registration.seats_requested} seat{registration.seats_requested === 1 ? '' : 's'}</span><em className={`is-${registration.participation_type.toLowerCase()}`}>{statusLabel(registration.participation_type)}</em><select aria-label={`Status for ${registration.full_name}`} value={registration.registration_status} onChange={(event) => updateRegistrationStatus(registration.id, event.target.value)}><option value="CONFIRMED">Confirmed</option><option value="CANCELLED">Cancelled</option></select>{!registration.email.endsWith('@privacy.invalid') && <button className="planner-registration-delete" onClick={() => deleteRegistrationData(registration)}>Delete personal data</button>}</article>)}</div>}
            </section>}
          </section>}
        </div>
      )}
      <EventForm open={showEventForm} busy={busy} initialValues={editingEvent} onClose={() => { setShowEventForm(false); setEditingEvent(null); }} onSave={saveEvent} onRemoveBanner={removeEventBanner} />
    </main>
  );
};

export default PlannerApp;
